import React, { useEffect, useState } from "react";
import { api } from "../../../lib/api";
import { D, JAKARTA } from "../ui";
import { AssetPreview, PreviewModal } from "./AssetPreview";
import { Palette, X, Eye, RefreshCw, Wand2, Check, Layers, Save } from "lucide-react";

// Modal detail aset — generate ulang (draf), kustomisasi, kelola varian
export function AssetDetail({ token, b, onClose, onChanged }) {
  const [prompt, setPrompt] = useState("");
  const [fullPrompt, setFullPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [draft, setDraft] = useState(null);
  const [vname, setVname] = useState("");
  const [pvVariant, setPvVariant] = useState(null); // {code, name} untuk preview varian

  useEffect(() => {
    if (b.has_draft) api.getDraft(token, b.bid).then(d => setDraft(d.draft)).catch(() => {});
  }, [b.bid]); // eslint-disable-line

  async function refresh() {
    const d = await api.catalog().catch(() => null);
    const nb = (d?.buildings || []).find(x => x.bid === b.bid);
    if (nb) onChanged(nb);
  }

  async function generate(mode, customPrompt) {
    setBusy(true);
    setMsg(mode === "edit"
      ? "Claude sedang memodifikasi bagian yang diminta... (~1-2 menit, jangan tutup jendela ini)"
      : "Claude melukis ulang aset dari nol... (~1-2 menit, jangan tutup jendela ini)");
    try {
      await api.generatePaint(token, b.bid, customPrompt || undefined, mode);
      let st = "running", guard = 0;
      while (st === "running" && guard < 90) {
        await new Promise(r => setTimeout(r, 3000));
        const s = await api.paintStatus(token, b.bid);
        st = s.status;
        guard++;
        if (st === "error") throw new Error(s.msg || "AI gagal");
      }
      if (st === "running") throw new Error("Terlalu lama — coba lagi");
      const d = await api.getDraft(token, b.bid);
      setDraft(d.draft);
      setMsg("Draf baru siap — tinjau, lalu terapkan / simpan sebagai varian / buang");
      refresh();
    } catch (e) { setMsg(e.message); }
    setBusy(false);
  }

  async function act(fn, okMsg, clearDraft = false) {
    try {
      await fn();
      setMsg(okMsg);
      setVname("");
      if (clearDraft) setDraft(null);
      refresh();
    } catch (e) { setMsg(e.message); }
  }

  async function removeAsset() {
    if (!window.confirm(`Hapus aset "${b.name}" beserta semua variannya?\nBangunan ini yang sudah terpasang di kota akan tampil sebagai kotak sederhana.`)) return;
    try {
      await api.deleteBuilding(token, b.bid);
      onClose();
    } catch (e) { setMsg(e.message); }
  }

  const pill = (tid, onClickFn, label, primary, disabled) => (
    <button key={tid} data-testid={tid} onClick={onClickFn} disabled={disabled}
      style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 18px", borderRadius: 99, fontSize: 12.5, fontWeight: 700, fontFamily: JAKARTA, cursor: disabled ? "default" : "pointer",
        background: primary ? D.blue : "#fff", color: primary ? "#fff" : D.ink, border: primary ? 0 : "1.5px solid #ece9dc", opacity: disabled ? 0.5 : 1, transition: "opacity 0.15s" }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.opacity = "0.85"; }}
      onMouseLeave={e => { if (!disabled) e.currentTarget.style.opacity = "1"; }}>
      {label}
    </button>
  );

  return (
    <div data-testid="asset-detail" onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(20,20,20,0.35)", backdropFilter: "blur(4px)", display: "grid", placeItems: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ width: "min(880px, 100%)", maxHeight: "88vh", overflowY: "auto", background: "#f8f6ee", borderRadius: 26, padding: "22px 24px", boxShadow: "0 24px 70px rgba(20,20,20,0.3)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 34, height: 34, borderRadius: 12, background: D.blueSoft, display: "grid", placeItems: "center" }}>
            <Palette size={16} color={D.blue} />
          </span>
          <div style={{ flex: 1 }}>
            <b data-testid="asset-detail-title" style={{ fontSize: 18, fontWeight: 800, color: D.ink }}>{b.name}</b>
            <p style={{ margin: 0, fontSize: 11.5, color: D.sub }}>{b.category} • {b.w}x{b.h} • {b.floors} lantai</p>
          </div>
          <button data-testid="asset-detail-close" onClick={onClose}
            style={{ width: 34, height: 34, borderRadius: "50%", border: "1.5px solid #e3dfd0", background: "#fff", display: "grid", placeItems: "center", cursor: "pointer", color: D.ink }}>
            <X size={15} />
          </button>
        </div>

        <div style={{ display: "flex", gap: 18, marginTop: 18, flexWrap: "wrap" }}>
          {/* Kolom preview */}
          <div style={{ flex: 1, minWidth: 280 }}>
            <p style={{ margin: "0 0 6px", fontSize: 11, fontWeight: 800, color: D.sub, letterSpacing: 1.5, textTransform: "uppercase" }}>Versi Aktif</p>
            <div style={{ background: "#fff", borderRadius: 18, overflow: "hidden", boxShadow: "0 6px 18px rgba(20,20,20,0.06)" }}>
              <AssetPreview def={b} height={210} />
            </div>
            {draft && (
              <div style={{ marginTop: 14 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "0 0 6px" }}>
                  <p style={{ margin: 0, fontSize: 11, fontWeight: 800, color: D.blue, letterSpacing: 1.5, textTransform: "uppercase" }}>Draf Baru</p>
                  <button data-testid="draft-preview-btn" onClick={() => setPvVariant({ code: draft.paintCode, name: "Draf Baru" })}
                    style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 99, border: `1.5px solid ${D.blueSoft}`, background: "#fff", color: D.blue, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: JAKARTA }}>
                    <Eye size={12} /> Preview besar
                  </button>
                </div>
                <div style={{ background: "#fff", borderRadius: 18, overflow: "hidden", boxShadow: `0 0 0 2px ${D.blueSoft}, 0 6px 18px rgba(20,20,20,0.06)` }}>
                  <AssetPreview def={b} draftCode={draft.paintCode} height={210} />
                </div>
                <p style={{ margin: "6px 2px 0", fontSize: 11, color: D.sub, fontStyle: "italic" }}>
                  {draft.mode === "edit" ? "Kustomisasi" : "Generate penuh"}{draft.prompt ? `: "${draft.prompt}"` : ""}
                </p>
              </div>
            )}
          </div>

          {/* Kolom aksi */}
          <div style={{ flex: 1, minWidth: 280 }}>
            <p style={{ margin: "0 0 4px", fontSize: 11, fontWeight: 800, color: D.sub, letterSpacing: 1.5, textTransform: "uppercase" }}>Generate Ulang Penuh</p>
            <p style={{ margin: "0 0 8px", fontSize: 11, color: "#a8a496" }}>AI melukis ulang aset dari nol. Kosongkan deskripsi untuk memakai spesifikasi standar katalog.</p>
            <textarea data-testid="studio-full-prompt" value={fullPrompt} onChange={e => setFullPrompt(e.target.value)}
              placeholder={`Opsional — deskripsikan hasil yang kamu bayangkan. Contoh: "kafe pinggir jalan bergaya industrial, ada teras kayu"`}
              style={{ width: "100%", minHeight: 56, padding: "12px 14px", background: "#fff", border: "1.5px solid #ece9dc", borderRadius: 14, color: "#141414", fontSize: 13, outline: "none", fontFamily: JAKARTA, resize: "vertical" }} />
            <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
              {pill("studio-generate", () => generate("full", fullPrompt), <><RefreshCw size={13} /> {busy ? "AI melukis..." : "Generate Ulang Penuh"}</>, true, busy)}
            </div>

            <div style={{ marginTop: 16, borderTop: "1px solid #e3dfd0", paddingTop: 14 }}>
              <p style={{ margin: "0 0 4px", fontSize: 11, fontWeight: 800, color: D.sub, letterSpacing: 1.5, textTransform: "uppercase" }}>Kustomisasi Sebagian</p>
              <p style={{ margin: "0 0 8px", fontSize: 11, color: "#a8a496" }}>AI hanya mengubah bagian yang kamu minta — sisanya dipertahankan persis.</p>
              <textarea data-testid="studio-prompt" value={prompt} onChange={e => setPrompt(e.target.value)}
                placeholder={`Contoh: "ganti atap jadi joglo merah", "warna dinding biru pastel", "halaman jadi taman bunga"`}
                style={{ width: "100%", minHeight: 66, padding: "12px 14px", background: "#fff", border: "1.5px solid #ece9dc", borderRadius: 14, color: "#141414", fontSize: 13, outline: "none", fontFamily: JAKARTA, resize: "vertical" }} />
              <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                {pill("studio-customize", () => generate("edit", prompt), <><Wand2 size={13} /> Buat Draf Kustomisasi</>, true, busy || !prompt.trim() || !b.paintCode)}
              </div>
            </div>
            {draft && (
              <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                {pill("draft-apply", () => act(() => api.applyDraft(token, b.bid), "Draf diterapkan sebagai versi aktif", true), <><Check size={13} /> Terapkan Draf</>, true)}
                {pill("draft-variant", () => act(() => api.draftToVariant(token, b.bid, vname), "Draf tersimpan sebagai varian", true), <><Layers size={13} /> Jadikan Varian</>, false)}
                {pill("draft-discard", () => act(() => api.discardDraft(token, b.bid), "Draf dibuang", true), "Buang", false)}
              </div>
            )}
            {msg && <p data-testid="studio-msg" style={{ fontSize: 12, color: "#b45309", fontWeight: 600, margin: "10px 2px 0" }}>{msg}</p>}

            <div style={{ marginTop: 18, borderTop: "1px solid #e3dfd0", paddingTop: 14 }}>
              <p style={{ margin: "0 0 8px", fontSize: 11, fontWeight: 800, color: D.sub, letterSpacing: 1.5, textTransform: "uppercase" }}>Simpan sebagai Varian</p>
              <div style={{ display: "flex", gap: 8 }}>
                <input data-testid="variant-name" value={vname} onChange={e => setVname(e.target.value)} placeholder="Nama varian (mis. Atap Joglo)"
                  style={{ flex: 1, padding: "10px 14px", background: "#fff", border: "1.5px solid #ece9dc", borderRadius: 99, fontSize: 12.5, outline: "none", fontFamily: JAKARTA }} />
                {pill("variant-save-active", () => act(() => api.saveVariant(token, b.bid, vname), "Versi aktif tersimpan sebagai varian"), <><Save size={13} /> Simpan Aktif</>, false, !b.paintCode)}
              </div>
              <p style={{ margin: "6px 2px 0", fontSize: 10.5, color: "#a8a496" }}>Menyimpan versi aktif saat ini. Jika ada draf, "Jadikan Varian" menyimpan drafnya.</p>
            </div>

            <div style={{ marginTop: 16 }}>
              <p style={{ margin: "0 0 8px", fontSize: 11, fontWeight: 800, color: D.sub, letterSpacing: 1.5, textTransform: "uppercase" }}>Varian Tersimpan ({(b.variants || []).length})</p>              {(b.variants || []).length === 0 && <p style={{ margin: 0, fontSize: 12, color: "#a8a496" }}>Belum ada varian.</p>}
              {(b.variants || []).map(v => (
                <div key={v.vid} data-testid={`variant-row-${v.vid}`}
                  style={{ display: "flex", alignItems: "center", gap: 10, background: "#fff", borderRadius: 14, padding: "8px 12px", marginBottom: 7, boxShadow: "0 3px 10px rgba(20,20,20,0.04)" }}>
                  <Layers size={13} color={D.blue} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <b style={{ display: "block", fontSize: 12.5, color: D.ink }}>{v.name}</b>
                    {v.prompt && <span style={{ fontSize: 10.5, color: D.sub, display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>"{v.prompt}"</span>}
                  </span>
                  <button data-testid={`variant-preview-${v.vid}`} onClick={() => setPvVariant({ code: v.paintCode, name: v.name })}
                    style={{ padding: "6px 12px", borderRadius: 99, border: "1.5px solid #ece9dc", background: "#fff", color: D.blue, fontSize: 11, fontWeight: 800, cursor: "pointer", fontFamily: JAKARTA, display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <Eye size={11} /> Preview</button>
                  <button data-testid={`variant-activate-${v.vid}`} onClick={() => act(() => api.activateVariant(token, b.bid, v.vid), `Varian "${v.name}" kini aktif di semua kota`)}
                    style={{ padding: "6px 12px", borderRadius: 99, border: 0, background: D.blueSoft, color: D.blue, fontSize: 11, fontWeight: 800, cursor: "pointer", fontFamily: JAKARTA }}>Aktifkan</button>
                  <button data-testid={`variant-delete-${v.vid}`} onClick={() => act(() => api.deleteVariant(token, b.bid, v.vid), "Varian dihapus")}
                    style={{ padding: "6px 12px", borderRadius: 99, border: "1.5px solid #fecaca", background: "#fff", color: "#dc2626", fontSize: 11, fontWeight: 800, cursor: "pointer", fontFamily: JAKARTA }}>Hapus</button>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 18, borderTop: "1px solid #f0d5d5", paddingTop: 14 }}>
              <p style={{ margin: "0 0 8px", fontSize: 11, fontWeight: 800, color: "#dc2626", letterSpacing: 1.5, textTransform: "uppercase" }}>Zona Berbahaya</p>
              <button data-testid="asset-delete" onClick={removeAsset}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 18px", borderRadius: 99, fontSize: 12.5, fontWeight: 700, fontFamily: JAKARTA, cursor: "pointer", background: "#fff", color: "#dc2626", border: "1.5px solid #fecaca", transition: "background-color 0.15s" }}
                onMouseEnter={e => e.currentTarget.style.background = "#fef2f2"}
                onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
                Hapus Aset Ini
              </button>
              <p style={{ margin: "6px 2px 0", fontSize: 10.5, color: "#a8a496" }}>Menghapus aset beserta seluruh variannya dari katalog secara permanen.</p>
            </div>
          </div>
        </div>
      </div>
      {pvVariant && <PreviewModal def={b} draftCode={pvVariant.code} label={pvVariant.name} onClose={() => setPvVariant(null)} />}
    </div>
  );
}
